import { z } from 'zod';
import { isoDateTimeSchema, sha256HexSchema, uuidSchema } from './common.js';
/**
 * Track lifecycle (admin-arch §2.4 / decisions §6). Values match the CHECK
 * constraint on `content.tracks.status` (migration 0008):
 * - uploading: tus upload in progress
 * - uploaded: master file landed in S3, awaiting transcode
 * - transcoding: worker actively producing HLS segments
 * - ready_for_review: ready, awaiting art-director approval
 * - published: visible to tenant clients (admin-arch §2.4 / decisions §168)
 * - rejected: art-director rejected; never visible
 * - requires_dup_review: soft-dup detection flagged for manual check
 * - archived: removed from active catalog but retained for audit
 * - withdrawn: pulled by legal / DMCA workflow
 */
export const trackStatusEnum = z.enum([
    'uploading',
    'uploaded',
    'transcoding',
    'ready_for_review',
    'published',
    'rejected',
    'archived',
    'requires_dup_review',
    'withdrawn',
]);
/**
 * BUG-203 — selector self-edit window (admin-arch §3.4: PATCH is
 * «admin / selector (свои до публикации)»).
 *
 * A selector may edit their own track until an admin accepts it: every
 * status before `published` qualifies, including `rejected` so the selector
 * can fix metadata and resubmit. Once accepted (or archived / withdrawn)
 * the card is read-only for the selector. Shared between the API guard in
 * `TracksService.patch` and the admin FE (edit-button visibility, Tags tab).
 */
export const SELECTOR_EDITABLE_TRACK_STATUSES = [
    'uploading',
    'uploaded',
    'transcoding',
    'ready_for_review',
    'requires_dup_review',
    'rejected',
];
export function isSelectorEditableTrackStatus(status) {
    return SELECTOR_EDITABLE_TRACK_STATUSES.includes(status);
}
/**
 * Field whitelist for selector PATCHes. Tier, status and legal stay
 * admin-only (CEO 2026-07-15) — otherwise a selector could self-publish or
 * set their track's tariff. The library is open to selectors (CEO 2026-10-29,
 * STORY-103) within the same fence as every other field: their own track,
 * before publication (`SELECTOR_EDITABLE_TRACK_STATUSES`).
 */
export const SELECTOR_PATCHABLE_TRACK_FIELDS = [
    'title',
    'artist',
    'year',
    'isrc',
    'vocal_language',
    'has_explicit_lyrics',
    'tags',
    'library',
];
/**
 * Library type — matches DB CHECK on `content.tracks.library_type`.
 * Values match architecture admin-arch §2.4 (`royalty_free` / `curated`).
 */
export const libraryTypeEnum = z.enum(['royalty_free', 'curated']);
export const tierAccessEnum = z.enum(['lite', 'standard', 'pro']);
export const hasExplicitLyricsEnum = z.enum(['yes', 'no', 'unknown']);
/**
 * Camelot key — 1A..12A or 1B..12B (case-insensitive). 24 fixed values,
 * not a tag (PRD §5.2 last paragraph).
 */
export const camelotKeySchema = z
    .string()
    .regex(/^(?:[1-9]|1[0-2])[ABab]$/, 'Expected Camelot key 1A..12B');
/**
 * Master metadata for a track (admin-arch content.tracks + track_versions
 * fields most consumers need). Versioned audio data lives in
 * `trackVersionSchema`; this entity tracks the catalog row itself.
 */
export const trackSchema = z.object({
    id: uuidSchema,
    title: z.string().min(1).max(500),
    artist: z.string().max(500).nullable(),
    release_year: z.number().int().min(1900).max(2100).nullable(),
    isrc: z.string().max(32).nullable(),
    vocal_language: z.string().max(8).nullable(),
    has_explicit_lyrics: hasExplicitLyricsEnum,
    duration_seconds: z.number().int().positive().nullable(),
    library_type: libraryTypeEnum,
    tier_access: tierAccessEnum,
    source_type: z.string().max(32).nullable(),
    legal_status: z.string().max(32).nullable(),
    status: trackStatusEnum,
    audio_sha256: sha256HexSchema.nullable(),
    uploaded_by: uuidSchema.nullable(),
    current_version_id: uuidSchema.nullable(),
    cover_s3_key: z.string().nullable(),
    /**
     * Public URL for the cover. BE populates this in list responses by
     * constructing the public S3 URL from `cover_s3_key`. Card response uses
     * a presigned URL (`cover_presigned_url`). Always nullable so callers can
     * gracefully fall back to a placeholder.
     */
    cover_url: z.string().url().nullable().optional(),
    custom_fields: z.record(z.string(), z.unknown()),
    meta: z.record(z.string(), z.unknown()),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
    deleted_at: isoDateTimeSchema.nullable(),
});
/**
 * A specific transcoded version of a track. Multiple versions exist when
 * an admin replaces the master (admin-arch §3.3 / replace-master).
 */
export const trackVersionSchema = z.object({
    id: uuidSchema,
    track_id: uuidSchema,
    version_number: z.number().int().positive(),
    bpm: z.number().int().positive().max(500).nullable(),
    camelot_key: camelotKeySchema.nullable(),
    lufs: z.number().nullable(),
    peak: z.number().nullable(),
    lra: z.number().nullable(),
    duration_seconds: z.number().int().positive().nullable(),
    sample_rate: z.number().int().positive().nullable(),
    file_size_bytes: z.number().int().positive().nullable(),
    transcode_status: z.enum(['queued', 'transcoding', 'ready', 'failed']),
    /**
     * S3 key of the top-level HLS-AES ABR manifest produced by the worker
     * (Wave 2.7 TASK-035b). NULL until the encryption pass finishes; never
     * served to end-clients verbatim — the player asks the API for a
     * signed URL via TASK-044.
     */
    hls_master_playlist_key: z.string().nullable(),
    retired_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
});
export const trackCardTagSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    category_id: uuidSchema,
    is_private: z.boolean(),
    source: z.enum(['id3', 'audio_analysis', 'filename', 'manual']),
});
/**
 * Wire-shape item returned by `GET /admin/tracks` (TASK-037a).
 *
 * Flattens fields the admin DataTable consumes in one row:
 *   - `bpm` / `camelot_key` come from the JOINed `track_versions` (current).
 *   - `selector_id` exposes `tracks.uploaded_by` under the FE-friendly alias.
 *   - `cover_url` is null until cover-art pipeline lands (TASK-034b).
 *   - `custom_fields` is empty in v0.1.0; TASK-037c will join
 *     `track_custom_field_values` once that table exists.
 *   - `tags` — the track's live applied tags, the same items as the card's
 *     `tags`, in the catalog's tag order: category order, then the tag's own
 *     order, then name (STORY-099).
 */
export const trackListItemSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    year: z.number().int().nullable(),
    bpm: z.number().nullable(),
    camelot_key: z.string().nullable(),
    duration_seconds: z.number().int().nullable(),
    library: libraryTypeEnum,
    tier: tierAccessEnum,
    status: trackStatusEnum,
    audio_sha256: z.string().nullable(),
    selector_id: uuidSchema.nullable(),
    uploaded_by_name: z.string().nullable(),
    cover_url: z.string().url().nullable(),
    custom_fields: z.record(z.string(), z.unknown()),
    tags: z.array(trackCardTagSchema),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
/**
 * Response envelope for `GET /admin/tracks`. Pagination mode is chosen by
 * the caller — see `trackFilterSchema.cursor` for the rules:
 *   - OFFSET mode (legacy, default when no `cursor` passed): `total` carries
 *     the row count from `count(*) OVER ()` so the UI can paint "Page X of
 *     Y"; `next_cursor` is `null`.
 *   - KEYSET mode (review M-5, default once FE migrates): `total` is `null`
 *     (the count subquery is the expensive part at large offset and is
 *     deliberately skipped); `next_cursor` carries the opaque token to feed
 *     into the next call. Absent when the page is the last one.
 *
 * `cursor` (legacy field) is kept for backwards compatibility — the Wave
 * 2.5 FE reads it as a presence-marker. New FE code uses `next_cursor`.
 */
export const trackListResponseSchema = z.object({
    items: z.array(trackListItemSchema),
    total: z.number().int().nonnegative().nullable(),
    cursor: z.string().nullable(),
    next_cursor: z.string().nullable(),
});
/**
 * Query parameters for `GET /admin/tracks` (TASK-037a + TASK-037b).
 *
 * `z.coerce.number()` accepts the URL-encoded strings Express delivers in
 * `req.query` and forwards them as numbers to the service layer.
 *
 * CSV fan-outs (`status_in`, `tags_any`, `tags_all`) accept either an
 * already-parsed array (Express has parsed it via `?key=a&key=b` syntax) or
 * a single comma-joined string — the latter is what the admin UI sends.
 *
 * Array caps (review C-1): clients cannot fan a `WHERE ... IN ($1,...,$N)`
 * with arbitrary N — Postgres parameter limit + planner cost grow with N.
 * Tag arrays cap at 50 (admin UI Filter sidebar exposes far fewer chips per
 * category); status_in caps at the size of the status enum (9 values).
 */
const TAGS_MAX = 50;
const STATUS_IN_MAX = 9; // matches trackStatusEnum cardinality
/**
 * Caps for custom_fields filter (TASK-037c, decisions §29). Each value is
 * compared with a `jsonb @> '{"<code>":"<value>"}'` containment predicate, so
 * the practical input set is small. Keys are validated against
 * `internal.custom_field_definitions` on the server (whitelist) — these caps
 * are defence-in-depth against pathologically large query strings.
 */
const CUSTOM_FIELDS_MAX_KEYS = 20;
const CUSTOM_FIELD_VALUE_MAX = 200;
const csvUuidArraySchema = (max) => z.preprocess((raw) => {
    if (raw === undefined || raw === null || raw === '')
        return undefined;
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === 'string')
        return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    return raw;
}, z.array(uuidSchema).max(max).optional());
// 24 Camelot values total, so the cap equals the full wheel.
const CAMELOT_KEYS_MAX = 24;
const csvCamelotKeyArraySchema = z.preprocess((raw) => {
    if (raw === undefined || raw === null || raw === '')
        return undefined;
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === 'string')
        return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    return raw;
}, z.array(camelotKeySchema).max(CAMELOT_KEYS_MAX).optional());
const csvStatusArraySchema = z.preprocess((raw) => {
    if (raw === undefined || raw === null || raw === '')
        return undefined;
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === 'string')
        return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    return raw;
}, z.array(trackStatusEnum).max(STATUS_IN_MAX).optional());
/**
 * Foundation for the selector moderation queue: filter the catalog by
 * `content.tracks.source_type` provenance (e.g. `selector_curated` /
 * `admin_upload`). `source_type` is a free-form `varchar(32)` column, so the
 * values are length-capped strings rather than an enum. Accepts a single
 * value or a comma-joined CSV (`?source_type=a` or `?source_type_in=a,b`);
 * both query keys feed this one filter. Capped at the status-enum cardinality
 * — the practical provenance set is small.
 */
const SOURCE_TYPE_MAX = 9;
const SOURCE_TYPE_VALUE_MAX = 32;
const csvSourceTypeArraySchema = z.preprocess((raw) => {
    if (raw === undefined || raw === null || raw === '')
        return undefined;
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === 'string')
        return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    return raw;
}, z.array(z.string().min(1).max(SOURCE_TYPE_VALUE_MAX)).max(SOURCE_TYPE_MAX).optional());
/**
 * Custom-field filter payload — `{ <code>: <value> }` map. Both code and
 * value are length-capped here; legitimate codes also have to pass the
 * runtime whitelist against `internal.custom_field_definitions`.
 *
 * Codes follow the same `^[a-z][a-z0-9_]*$` regex enforced by
 * `customFieldCreateSchema` (custom-field.ts) so the wire form matches what
 * the admin can register.
 *
 * Wire form (recommended): `?custom_fields=<urlencoded-json>` — frontend
 * serialises the object as JSON. The server-side preprocessor in
 * `trackFilterSchema` accepts either a JSON string or an already-parsed
 * object (Express does not parse JSON in query strings by default).
 */
export const trackCustomFieldsFilterSchema = z
    .record(z.string().min(1).max(64).regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case code'), z.union([
    z.string().min(1).max(CUSTOM_FIELD_VALUE_MAX),
    z.number(),
    z.boolean(),
]))
    .refine((obj) => Object.keys(obj).length <= CUSTOM_FIELDS_MAX_KEYS, `At most ${CUSTOM_FIELDS_MAX_KEYS} custom-field keys per query`);
const customFieldsFilterPreprocessor = z.preprocess((raw) => {
    if (raw === undefined || raw === null || raw === '')
        return undefined;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        }
        catch {
            return raw; // let downstream schema reject it as a validation error
        }
    }
    return raw;
}, trackCustomFieldsFilterSchema.optional());
export const trackFilterSchema = z.object({
    library: libraryTypeEnum.optional(),
    status_in: csvStatusArraySchema,
    tier: tierAccessEnum.optional(),
    bpm_min: z.coerce.number().int().positive().max(500).optional(),
    bpm_max: z.coerce.number().int().positive().max(500).optional(),
    // BUG-162: the Camelot grid in the catalog sidebar is multi-select and the
    // FE has always sent `keys` as a CSV — `keys` is the canonical wire param.
    // The never-used single `key` param was removed with the same fix.
    keys: csvCamelotKeyArraySchema,
    date_from: isoDateTimeSchema.optional(),
    date_to: isoDateTimeSchema.optional(),
    tags_any: csvUuidArraySchema(TAGS_MAX),
    tags_all: csvUuidArraySchema(TAGS_MAX),
    // STORY-099 «Без тегов»: only tracks without live tags. Only the string `true` turns it on;
    // `z.coerce.boolean()` would read `'false'` as true.
    untagged: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
    q: z.string().min(1).max(200).optional(),
    selector_id: uuidSchema.optional(),
    // Foundation for the moderation queue (filter by track provenance). Both
    // `source_type` and the `source_type_in` alias map to the same predicate;
    // the service reads `source_type ?? source_type_in`.
    source_type: csvSourceTypeArraySchema,
    source_type_in: csvSourceTypeArraySchema,
    custom_fields: customFieldsFilterPreprocessor,
    page: z.coerce.number().int().nonnegative().default(0),
    size: z.coerce.number().int().positive().max(200).default(50),
    sort: z
        .enum([
        'created_at',
        'title',
        'artist',
        'bpm',
        'status',
        'duration_seconds',
        'library',
        'tier',
        'updated_at',
    ])
        .default('created_at'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    // Wave 2.6 review M-5: keyset pagination cursor. Opaque base64-encoded
    // JSON `{ created_at: ISO8601, id: uuid }` minted by the API in
    // `next_cursor`. When present, the service switches from OFFSET +
    // count(*) OVER () (slow at large offsets) to a (created_at, id) keyset
    // scan with no total — significantly faster past page 100. When absent,
    // the legacy OFFSET behaviour is preserved so Wave 2.5 FE keeps working.
    // Cap of 200 chars guards against pathological inputs (a valid token is
    // ~80 chars after base64).
    cursor: z.string().max(200).optional(),
});
/**
 * Body schema for `PATCH /admin/tracks/:id` (TASK-039).
 *
 * Status transitions are validated server-side against the state machine
 * (admin-arch §3.5) — clients send any allowed status, service rejects
 * forbidden transitions with 422.
 *
 * `tags.add` / `tags.remove` express a diff; the service applies it inside
 * a transaction so partial failures are atomic.
 *
 * `custom_fields` is deliberately omitted in v0.1.0 — see TASK-037c.
 */
/**
 * Caps for the Wave 2.10 TASK-053d legal text fields. URL is short, license
 * body and retire reason are admin free-form but bounded so a runaway paste
 * can't blow the audit_log JSONB row size.
 */
const CONTRACT_LINK_MAX = 2048;
const LICENSE_TEXT_MAX = 20_000;
const RETIRE_REASON_MAX = 2000;
/**
 * ISRC format — `CC-XXX-YY-NNNNN` (12 alphanumeric chars when dashes are
 * stripped). Stored as the canonical 12-char form (no dashes); the regex
 * accepts both the dashed and the canonical form so admins can paste either.
 */
const ISRC_REGEX = /^[A-Z]{2}-?[A-Z0-9]{3}-?[0-9]{2}-?[0-9]{5}$/;
export const trackPatchInputSchema = z
    .object({
    title: z.string().min(1).max(500).optional(),
    artist: z.string().min(1).max(500).nullable().optional(),
    year: z.number().int().min(1900).max(2100).nullable().optional(),
    library: libraryTypeEnum.optional(),
    tier: tierAccessEnum.optional(),
    status: trackStatusEnum.optional(),
    /**
     * BUG-083: identification fields previously read-only on the FE.
     *   * `isrc` — uppercase 12-char identifier (dashes optional on the wire);
     *     `null` clears the column. Optional even for curated tracks at the
     *     wire level — the BE may enforce a stricter rule per
     *     `legal_status='licensed'` in a follow-up.
     *   * `vocal_language` — ISO 639-1 code (`ru`, `en`, ...) or the sentinel
     *     `instrumental` / `multi`; capped at 8 chars to match the DB column.
     *   * `has_explicit_lyrics` — `yes` / `no` / `unknown` enum (DB CHECK).
     */
    isrc: z
        .string()
        .trim()
        .regex(ISRC_REGEX, 'Expected ISRC in CC-XXX-YY-NNNNN format')
        .nullable()
        .optional(),
    vocal_language: z.string().trim().min(1).max(8).nullable().optional(),
    has_explicit_lyrics: hasExplicitLyricsEnum.optional(),
    tags: z
        .object({
        add: z.array(uuidSchema).max(TAGS_MAX).optional(),
        remove: z.array(uuidSchema).max(TAGS_MAX).optional(),
    })
        .optional(),
    /**
     * Wave 2.10 TASK-053d: grouped legal fields. Nested under `legal` so
     * the FE Legal tab can submit them as a coherent block and the service
     * can apply them with a single SET clause. Each property is independently
     * optional; passing `null` clears the column. Strict mode rejects unknown
     * keys inside the nested object too.
     */
    legal: z
        .object({
        contract_link: z.string().max(CONTRACT_LINK_MAX).nullable().optional(),
        license_text: z.string().max(LICENSE_TEXT_MAX).nullable().optional(),
        retire_reason: z.string().max(RETIRE_REASON_MAX).nullable().optional(),
    })
        .strict()
        .refine((value) => Object.keys(value).length > 0, { message: 'legal payload must contain at least one field' })
        .optional(),
})
    .strict()
    .refine((value) => Object.keys(value).length > 0, { message: 'PATCH body must contain at least one field' });
/**
 * Wire shapes for `GET /admin/tracks/:id` track-card composite (TASK-038,
 * admin-arch §3.3, PRD §3.6).
 *
 * The endpoint returns one composite payload covering all six card tabs:
 *   - `overview`        — `tracks` columns + current `track_versions` metrics
 *   - `versions`        — every `track_versions` row (table for "Files & versions")
 *   - `tags`            — applied tags with `source` provenance
 *   - `audit_history`   — last 100 `internal.audit_log` entries for this id
 *   - `legal`           — legal/contract subset of `tracks` columns
 *   - `playback`        — discriminated union; until the daily-stats table
 *                          ships (future wave), the server returns
 *                          `{ available: false, message }`.
 */
export const trackCardVersionSchema = z.object({
    id: uuidSchema,
    version_number: z.number().int().positive(),
    is_current: z.boolean(),
    transcode_status: z.enum(['queued', 'transcoding', 'ready', 'failed']),
    bpm: z.number().int().nullable(),
    camelot_key: z.string().nullable(),
    sample_rate: z.number().int().nullable(),
    duration_seconds: z.number().int().nullable(),
    file_size_bytes: z.number().int().nullable(),
    master_file_key: z.string(),
    created_at: isoDateTimeSchema,
    created_by_user_id: uuidSchema.nullable(),
    /**
     * Wave 2.10 TASK-053d: human-readable author label resolved by the API
     * via `LEFT JOIN internal.users u ON u.id = track_versions.created_by`.
     * `null` when `created_by_user_id` itself is null. Soft-deleted users
     * surface as `'<удалённый пользователь>'` so the audit chain stays
     * legible without leaking the original name.
     */
    created_by_display_name: z.string().nullable(),
});
export const trackCardAuditEntrySchema = z.object({
    id: uuidSchema,
    occurred_at: isoDateTimeSchema,
    actor_user_id: uuidSchema.nullable(),
    actor_role: z.string().nullable(),
    action: z.string(),
    changes: z.unknown().nullable(),
    /**
     * Wave 2.10 TASK-053d: human-readable actor label resolved by the API
     * via `LEFT JOIN internal.users u ON u.id = audit_log.actor_user_id`.
     * `null` when `actor_user_id` itself is null (system actor). Soft-deleted
     * users surface as `'<удалённый пользователь>'` (same convention as
     * `trackCardVersionSchema.created_by_display_name`).
     */
    actor_display_name: z.string().nullable(),
});
/**
 * «Обрезка тишины» on the track card (track-transitions arch §8), in the order
 * the API decides it:
 *   * `not_processed` — «ещё не обработан»: the current version has no analysis;
 *                       or it was analysed, trimming is on, and its trimmed copy
 *                       is not built yet (STORY-079);
 *   * `failed`        — «анализ не удался»;
 *   * `no_sound`      — «звучание не найдено»;
 *   * `off`           — «выключена — звук не изменён»: analysed, trimming off;
 *   * `applied`       — «применена»: analysed, trimming on, and the current
 *                       version has an active trimmed copy — venues receive it
 *                       (STORY-079).
 */
export const trackSilenceAnalysisStatusEnum = z.enum([
    'not_processed',
    'failed',
    'no_sound',
    'off',
    'applied',
]);
export const trackNoPointReasonEnum = z.enum(['no_fade', 'short_track']);
export const trackInnerSilenceSchema = z.object({
    start_sec: z.number().nonnegative(),
    end_sec: z.number().nonnegative(),
});
/**
 * What the latest analysis of the track's current version found. Seconds, not
 * rounded — the card formats them.
 *
 * Every number is null and `inner_silences` is empty unless `status` is `off`
 * or `applied`. Then `lead_sec` and `tail_sec` are the silence before the first
 * sound and after the last one, and exactly one of `point_before_end_sec` (the
 * transition point, counted back from the last sound) and `no_point_reason` is
 * set. `long_ending_sec` repeats `point_before_end_sec` when it is above the
 * long-ending threshold the analysis ran with, and is null otherwise.
 */
export const trackSilenceAnalysisSchema = z.object({
    status: trackSilenceAnalysisStatusEnum,
    lead_sec: z.number().nonnegative().nullable(),
    tail_sec: z.number().nonnegative().nullable(),
    point_before_end_sec: z.number().nonnegative().nullable(),
    no_point_reason: trackNoPointReasonEnum.nullable(),
    inner_silences: z.array(trackInnerSilenceSchema),
    long_ending_sec: z.number().nonnegative().nullable(),
});
export const trackCardOverviewSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    release_year: z.number().int().nullable(),
    isrc: z.string().nullable(),
    vocal_language: z.string().nullable(),
    has_explicit_lyrics: hasExplicitLyricsEnum,
    duration_seconds: z.number().int().nullable(),
    library_type: libraryTypeEnum,
    tier_access: tierAccessEnum,
    status: trackStatusEnum,
    audio_sha256: z.string().nullable(),
    uploaded_by: uuidSchema.nullable(),
    cover_s3_key: z.string().nullable(),
    /**
     * Wave 2.10 TASK-053d: short-lived (10 min) GET presigned URL for the
     * cover stored at `cover_s3_key`. `null` when `cover_s3_key` is null.
     * TTL mirrors `PREVIEW_KEY_TOKEN_TTL_MS` so the FE only needs one
     * refresh policy for short-lived asset URLs.
     */
    cover_presigned_url: z.string().nullable(),
    // Identification / technical fields surfaced from current track_versions:
    bpm: z.number().int().nullable(),
    camelot_key: z.string().nullable(),
    sample_rate: z.number().int().nullable(),
    lufs: z.number().nullable(),
    peak: z.number().nullable(),
    lra: z.number().nullable(),
    // Null only while the track has no current version; the card shows that as
    // `not_processed`.
    silence_analysis: trackSilenceAnalysisSchema.nullable(),
    // JSONB blobs — `meta` is intended for free-form admin annotations
    // (internal note, retire reason, etc.); `custom_fields` is the
    // user-defined extra columns map (decisions §29).
    custom_fields: z.record(z.string(), z.unknown()),
    meta: z.record(z.string(), z.unknown()),
    // Wave 2.63 BE-1 (TASK-222-followup-be-flag): soft-dup match recorded
    // at upload time (`content.uploads.summary->>'duplicate_of_track_id'`).
    // Surfaced here so the FE can render the "Возможно дубликат" banner on
    // the track card without a separate uploads round-trip. Null when the
    // track has no pending duplicate match (most published tracks).
    duplicate_of_track_id: uuidSchema.nullable(),
    duplicate_of_title: z.string().nullable(),
    duplicate_of_artist: z.string().nullable(),
    duplicate_of_duration_seconds: z.number().int().nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
export const trackCardLegalSchema = z.object({
    legal_status: z.string().nullable(),
    source_type: z.string().nullable(),
    isrc: z.string().nullable(),
    /**
     * Wave 2.10 TASK-053d: full free-form legal fields on `content.tracks`.
     *   * `contract_link`  — URL to the signed contract / license PDF.
     *   * `license_text`   — full license body inline (royalty-free templates).
     *   * `retire_reason`  — justification for moving to archived / withdrawn.
     * All three are admin-editable via the same `PATCH /admin/tracks/:id`
     * endpoint under the nested `legal: { ... }` payload (see
     * `trackPatchInputSchema`).
     */
    contract_link: z.string().nullable(),
    license_text: z.string().nullable(),
    retire_reason: z.string().nullable(),
});
/**
 * Discriminated union for the Playback tab. Until
 * `content.playback_daily_track_stats` ships, the server returns the
 * `available: false` branch with a stable message field. When the table
 * lands, a future revision will switch the discriminator to `true` and
 * widen the payload.
 */
export const trackCardPlaybackUnavailableSchema = z.object({
    available: z.literal(false),
    message: z.string(),
});
export const trackCardPlaybackAvailableSchema = z.object({
    available: z.literal(true),
    plays_30d: z.number().int().nonnegative(),
    skips_30d: z.number().int().nonnegative(),
    total_seconds_30d: z.number().int().nonnegative(),
});
export const trackCardPlaybackSchema = z.discriminatedUnion('available', [
    trackCardPlaybackUnavailableSchema,
    trackCardPlaybackAvailableSchema,
]);
export const trackCardResponseSchema = z.object({
    overview: trackCardOverviewSchema,
    versions: z.array(trackCardVersionSchema),
    tags: z.array(trackCardTagSchema),
    audit_history: z.array(trackCardAuditEntrySchema),
    legal: trackCardLegalSchema,
    playback: trackCardPlaybackSchema,
});
/**
 * Wave 2.18 BE-2 (TASK-043b) — `POST /admin/tracks/:id/swap-version` body.
 *
 * `new_version_id` MUST belong to the same track AND have
 * `transcode_status='ready'` — both invariants enforced server-side; client
 * violations surface as 409 with a structured code so the FE can show a
 * precise toast.
 *
 * `retire_old_at_iso` is optional — when omitted the service defaults to
 * `now() + 24h` (PRD §3.3 "Заменить мастер" overlap window). Caller may pass
 * a future timestamp up to 7 days out; past timestamps OR > 7 days → 422.
 */
export const swapVersionRequestSchema = z
    .object({
    new_version_id: uuidSchema,
    retire_old_at_iso: isoDateTimeSchema.optional(),
})
    .strict();
export const swapVersionResponseSchema = z
    .object({
    id: uuidSchema,
    current_version_id: uuidSchema,
    previous_version_id: uuidSchema.nullable(),
    previous_retire_at: isoDateTimeSchema.nullable(),
})
    .strict();
/**
 * Wave 2.18 BE-2 — version row with retire metadata, returned by future
 * versions-list endpoints + embedded in audit chain payloads. `is_current`
 * + `retire_at` + `retired_at` mirror the new `content.track_versions`
 * columns introduced by migration 0027.
 */
export const trackVersionListItemSchema = z
    .object({
    id: uuidSchema,
    track_id: uuidSchema,
    version_number: z.number().int().positive(),
    is_current: z.boolean(),
    transcode_status: z.enum(['queued', 'transcoding', 'ready', 'failed']),
    master_file_key: z.string(),
    file_size_bytes: z.number().int().nullable(),
    retire_at: isoDateTimeSchema.nullable(),
    retired_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
    created_by_user_id: uuidSchema.nullable(),
})
    .strict();
/**
 * BUG-051 — admin master-download response (`GET /admin/tracks/:id/master/download`).
 *
 * Backend mints a short-lived S3 GET presigned URL (TTL =
 * `PREVIEW_KEY_TOKEN_TTL_MS` — 10 min, same horizon as the admin HLS preview
 * key so the FE keeps a single refresh policy for short-lived asset URLs).
 * The FE drives an `<a download>` click against `presigned_url`;
 * `expires_at` lets the UI re-mint if the user clicked away.
 *
 * Selectors get 403 `MASTER_DOWNLOAD_FORBIDDEN` (master files are the
 * pristine royalty-free / curated source and must not be leakable via the
 * selector role). Admin / super_admin only.
 */
export const trackMasterDownloadResponseSchema = z
    .object({
    presigned_url: z.string().url(),
    expires_at: isoDateTimeSchema,
})
    .strict();
/**
 * BUG-038 — per-track playback stats (`GET /admin/tracks/:id/playback-stats`).
 *
 * Aggregates `content.play_events_daily` over `[from, to]` (date strings,
 * `YYYY-MM-DD`). When `from`/`to` are omitted the server defaults to the
 * trailing 30 days. Date strings are inclusive on both ends.
 *
 * Wire payload:
 *   - `total_plays`  — sum of `total_plays` over the window.
 *   - `per_venue`    — non-zero venues, sorted by `plays DESC, venue_name ASC`.
 *                      `venue_name` falls back to a placeholder when the
 *                      venue row was soft-deleted (audit chain stays legible).
 *   - `timeline`     — one row per calendar day in `[from, to]`. Days with no
 *                      plays are zero-filled so the FE chart renders a
 *                      continuous x-axis without gaps.
 *
 * Selectors see stats only for tracks they uploaded (defence-in-depth on top
 * of `content.tracks` RLS). Admin / super_admin / selector(own) → 200; other
 * selectors → 403 `TRACK_FORBIDDEN`; soft-deleted track → 404 `TRACK_NOT_FOUND`.
 */
export const trackPlaybackStatsDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, 'Date must be in YYYY-MM-DD form');
export const trackPlaybackStatsQuerySchema = z
    .object({
    from: trackPlaybackStatsDateSchema.optional(),
    to: trackPlaybackStatsDateSchema.optional(),
})
    .strict()
    .refine((q) => !q.from || !q.to || q.from <= q.to, { message: '`from` must be on or before `to`', path: ['from'] });
export const trackPlaybackStatsVenueEntrySchema = z
    .object({
    venue_id: uuidSchema,
    venue_name: z.string(),
    plays: z.number().int().nonnegative(),
})
    .strict();
export const trackPlaybackStatsTimelineEntrySchema = z
    .object({
    date: trackPlaybackStatsDateSchema,
    plays: z.number().int().nonnegative(),
})
    .strict();
/**
 * BUG-278 — the feedback half of the "Воспроизведения" tab (PRD §5.4:
 * "счётчик плеев по дням, дизлайки, лайки, доля пропусков").
 *
 * `total_skips` and `skip_rate` are windowed like `total_plays` (both come
 * from `content.play_events_daily` over `[from, to]`). The rollup's
 * `total_plays` counts only completed plays — a skip is NOT one of them —
 * so the rate is `skips / (plays + skips)`: the share of starts that got
 * skipped. It is `null`, not `0`, when the window has no starts at all:
 * "no data" and "nothing was skipped" are different answers and the tile must
 * not claim the second when it means the first.
 *
 * `likes` / `dislikes` are NOT windowed: they count currently active rows in
 * `customers.track_likes` / `customers.track_dislikes` (`removed_at IS NULL`)
 * across every venue. A dislike is a standing block on the track in that
 * venue (BUG-277), so "how many venues block this track right now" is the
 * question the catalog operator actually has; a 30-day window would answer a
 * different one.
 */
export const trackFeedbackStatsSchema = z
    .object({
    likes: z.number().int().nonnegative(),
    dislikes: z.number().int().nonnegative(),
})
    .strict();
export const trackPlaybackStatsResponseSchema = z
    .object({
    from: trackPlaybackStatsDateSchema,
    to: trackPlaybackStatsDateSchema,
    total_plays: z.number().int().nonnegative(),
    total_skips: z.number().int().nonnegative(),
    skip_rate: z.number().min(0).max(1).nullable(),
    feedback: trackFeedbackStatsSchema,
    per_venue: z.array(trackPlaybackStatsVenueEntrySchema),
    timeline: z.array(trackPlaybackStatsTimelineEntrySchema),
})
    .strict();
//# sourceMappingURL=track.js.map